// src/components/nav/Navbar.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supaBaseClient"; // <-- add this
import { API_BASE } from "../../config";
import { SlArrowLeftCircle, SlUser } from "react-icons/sl";

const Navbar = ({ userUID, setUserUID, userRole }) => {
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false); // used as mobile dropdown open state on small screens

  const navigate = useNavigate();

  const profileDropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    let unsub = () => {};

    const fetchUserData = async () => {
      if (!userUID || !userRole) {
        setUserData(null);
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API_BASE}/api/users/${userUID}`);
        setUserData(response.data);
      } catch (err) {
        console.error("Error fetching user data:", err);
        setError("Failed to load user data.");
      } finally {
        setLoading(false);
      }
    };

    // initial load
    fetchUserData();

    // listen for auth/profile changes to refresh dropdown immediately
    const { data } = supabase.auth.onAuthStateChange(() => {
      // when tokens/user updates, refetch
      fetchUserData();
    });
    unsub = () => data.subscription.unsubscribe();

    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsDrawerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      try { unsub(); } catch {}
    };
  }, [userUID, userRole]);

  // ---- FIXED LOGOUT ----
const handleLogout = async () => {
  try {
    // Prefer local logout (clears this device only, no cross-site call)
    const { error } = await supabase.auth.signOut({ scope: "local" });
    if (error) throw error;
  } catch (e) {
    console.warn("Local signOut had an issue; continuing with client cleanup:", e);
  } finally {
    // Always clear your app state
    localStorage.removeItem("userUID");
    setUserUID(null);
    setIsProfileDropdownOpen(false);
    navigate("/signin", { replace: true });
  }
};
  const isAdmin = userData?.role === "admin";

  return (
    <nav className="bg-teal-600 text-white shadow-md sticky top-0 z-40" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <div className="mx-auto flex items-center justify-between py-4 px-4 md:py-6 md:px-8 relative">
        <div className="relative flex items-center" ref={mobileMenuRef}>
          <span
            onClick={() => {
              if (window.innerWidth < 768) {
                setIsDrawerOpen((p) => !p);
              } else {
                isAdmin ? navigate("/admintopics") : navigate("/home");
              }
            }}
            className="text-xl md:text-2xl font-bold cursor-pointer select-none"
          >
            MLMaths
          </span>
          {/* Explicit mobile toggle next to logo */}
          <button
            type="button"
            className="md:hidden ml-2 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-white/50"
            aria-label="Toggle menu"
            onClick={() => setIsDrawerOpen((p) => !p)}
          >
            {/* simple chevron indicator */}
            <span className={`inline-block transition-transform ${isDrawerOpen ? 'rotate-180' : ''}`}>▾</span>
          </button>
          {/* Mobile dropdown anchored to logo */}
          {isDrawerOpen && (
            <div className="md:hidden absolute left-0 top-full mt-2 w-[92vw] sm:w-72 bg-white text-teal-700 shadow-2xl rounded-xl border border-gray-200 z-[9999] max-h-[80vh] overflow-y-auto overscroll-contain ios-scroll-touch">
              <div className="py-1">
                {isAdmin ? (
                  <>
                    <button onClick={() => { setIsDrawerOpen(false); navigate('/admintopics'); }} className="block w-full text-left py-3 px-5 text-base leading-6 hover:bg-teal-50">Topics</button>
                    <button onClick={() => { setIsDrawerOpen(false); navigate('/adminexam'); }} className="block w-full text-left py-3 px-5 text-base leading-6 hover:bg-teal-50">Exam Papers</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => { setIsDrawerOpen(false); navigate('/home'); }} className="block w-full text-left py-3 px-5 text-base leading-6 hover:bg-teal-50">Home</button>
                    <button onClick={() => { setIsDrawerOpen(false); navigate('/topicspage'); }} className="block w-full text-left py-3 px-5 text-base leading-6 hover:bg-teal-50">Topics</button>
                    <button onClick={() => { setIsDrawerOpen(false); navigate('/progress'); }} className="block w-full text-left py-3 px-5 text-base leading-6 hover:bg-teal-50">Progress</button>
                  </>
                )}
                <div className="my-1 border-t border-gray-100" />
                <button onClick={() => { setIsDrawerOpen(false); navigate('/profile'); }} className="block w-full text-left py-3 px-5 text-base leading-6 hover:bg-teal-50">Edit Profile</button>
                <button onClick={handleLogout} className="block w-full text-left py-3 px-5 text-base leading-6 text-red-600">
                  <span className="md:flex md:items-center md:space-x-2 block">
                    <span className="md:inline hidden">↩</span>
                    <span>Logout</span>
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Desktop nav */}
        <div className="hidden md:flex space-x-10 items-center">
          {isAdmin ? (
            <>
              <span onClick={() => navigate("/admintopics")} className="text-lg font-semibold text-white cursor-pointer">
                Topics
              </span>
              <span onClick={() => navigate("/adminexam")} className="text-lg font-semibold text-white cursor-pointer">
                Exam Papers
              </span>
            </>
          ) : (
            <>
              <span onClick={() => navigate("/home")} className="text-lg font-semibold text-white cursor-pointer">
                Home
              </span>

              <span onClick={() => navigate("/topicspage")} className="text-lg font-semibold text-white cursor-pointer">
                Topics
              </span>

              <span onClick={() => navigate("/progress")} className="text-lg font-semibold text-white cursor-pointer">
                Progress
              </span>
            </>
          )}

          <div className="relative" ref={profileDropdownRef}>
            <button onClick={() => setIsProfileDropdownOpen((p) => !p)} className="text-lg font-semibold text-white">
              Profile
            </button>
            {isProfileDropdownOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white shadow-lg rounded-xl p-6 z-10 border border-gray-200">
                {loading ? (
                  <p className="text-gray-500">Loading...</p>
                ) : error ? (
                  <p className="text-red-500">{error}</p>
                ) : (
                  userData && (
                    <>
                      <div className="flex items-center space-x-4 border-b pb-4 mb-4">
                        <img src="/userprofile.jpg" alt="User Avatar" className="w-12 h-12 rounded-full" />
                        <div>
                          <p className="font-bold text-gray-700">{userData.first_name} {userData.last_name}</p>
                          <p className="text-sm text-gray-500">{userData.role === "admin" ? "Role: Admin" : `Year: ${userData.school_year}`}</p>
                          <p className="text-sm text-gray-500">{userData.role === "admin" ? "" : `Level: ${userData.level}`}</p>
                        </div>
                      </div>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-center space-x-2 cursor-pointer px-4 py-2 hover:bg-gray-100 rounded-md" onClick={() => { setIsProfileDropdownOpen(false); navigate("/profile"); }}>
                          <SlUser className="text-lg" /><span>Edit Profile</span>
                        </li>
                        <li className="flex items-center space-x-2 cursor-pointer px-4 py-2 hover:bg-gray-100 rounded-md" onClick={handleLogout}>
                          <SlArrowLeftCircle className="text-lg" /><span>Logout</span>
                        </li>
                      </ul>
                    </>
                  )
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile dropdown replaces drawer; nothing to render here */}
      </div>
    </nav>
  );
};

export default Navbar;